import { useSession } from "@clerk/clerk-react";
import {
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  Gavel,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import {
  LegalProcedure,
  ProcedureDate,
  ProcedureDateType,
  LEGAL_PROCEDURE_STATUS_LABELS,
  LEGAL_PROCEDURE_STATUS_COLORS,
  LEGAL_PROCEDURE_TYPE_LABELS,
  PARTY_ROLE_LABELS,
} from "shared/types/legal-procedure";
import { Link } from "react-router-dom";
import { applyMask } from "shared/utils/masks";
import { useUsers } from "shared/contexts/users.context";
import { LegalProcedureEditForm } from "./LegalProcedureEditForm";
import { ProcedureIssues } from "./ProcedureIssues";
import { ProcedureTimeline } from "./ProcedureTimeline";

interface LegalProcedureDetailProps {
  procedure: LegalProcedure;
  onProcedureUpdated: (procedure: LegalProcedure) => void;
  onProcedureDeleted: (procedureId: number) => void;
}

export const LegalProcedureDetail = ({
  procedure,
  onProcedureUpdated,
  onProcedureDeleted,
}: LegalProcedureDetailProps) => {
  const { session } = useSession();
  const { users } = useUsers();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingDate, setIsSavingDate] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getUserName = (userId: string | null | undefined) => {
    if (!userId) return null;
    const user = users.find((u) => String(u.id) === String(userId));
    if (!user) return null;
    return `${user.first_name} ${user.last_name}`;
  };

  const handleSaveEdit = useCallback(
    async (updates: Record<string, unknown>) => {
      setIsSavingEdit(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/${procedure.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updates),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update procedure: ${response.status}`);
        }

        const updatedProcedure = await response.json();
        onProcedureUpdated({
          ...updatedProcedure,
          procedureDates: procedure.procedureDates,
        });
        setIsEditing(false);
        toast.success("Procedure bijgewerkt");
      } catch (error) {
        console.error("Error updating procedure:", error);
        toast.error("Fout bij het bijwerken van procedure");
      } finally {
        setIsSavingEdit(false);
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleAddDate = useCallback(
    async (data: {
      type: ProcedureDateType;
      date: string;
      remarks?: string;
    }) => {
      setIsSavingDate(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/${procedure.id}/dates`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to add procedure date: ${response.status}`);
        }

        const newDate: ProcedureDate = await response.json();
        onProcedureUpdated({
          ...procedure,
          procedureDates: [...procedure.procedureDates, newDate],
        });
        toast.success("Gebeurtenis toegevoegd");
      } catch (error) {
        console.error("Error adding procedure date:", error);
        toast.error("Fout bij het toevoegen van gebeurtenis");
      } finally {
        setIsSavingDate(false);
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleToggleDateCompleted = useCallback(
    async (dateItem: ProcedureDate) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/dates/${dateItem.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ completed: !dateItem.completed }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update procedure date: ${response.status}`);
        }

        onProcedureUpdated({
          ...procedure,
          procedureDates: procedure.procedureDates.map((pd) =>
            pd.id === dateItem.id ? { ...pd, completed: !pd.completed } : pd
          ),
        });
      } catch (error) {
        console.error("Error toggling procedure date:", error);
        toast.error("Fout bij het bijwerken van gebeurtenis");
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleDeleteDate = useCallback(
    async (dateId: number) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/dates/${dateId}`,
          { method: "DELETE" },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to delete procedure date: ${response.status}`);
        }

        onProcedureUpdated({
          ...procedure,
          procedureDates: procedure.procedureDates.filter(
            (pd) => pd.id !== dateId
          ),
        });
        toast.success("Gebeurtenis verwijderd");
      } catch (error) {
        console.error("Error deleting procedure date:", error);
        toast.error("Fout bij het verwijderen van gebeurtenis");
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleToggleConclusieTermijnen = useCallback(
    async (value: boolean) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/${procedure.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ hasConclusieTermijnen: value }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update procedure: ${response.status}`);
        }

        onProcedureUpdated({
          ...procedure,
          hasConclusieTermijnen: value,
        });
      } catch (error) {
        console.error("Error updating conclusietermijnen:", error);
        toast.error("Fout bij het bijwerken");
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleIssuesChanged = useCallback(
    async (issues: string[]) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures/${procedure.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ issues }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update issues: ${response.status}`);
        }

        onProcedureUpdated({
          ...procedure,
          issues,
        });
      } catch (error) {
        console.error("Error updating issues:", error);
        toast.error("Fout bij het bijwerken van problemen");
      }
    },
    [procedure, session, onProcedureUpdated]
  );

  const handleDeleteProcedure = useCallback(async () => {
    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/legal-procedures/${procedure.id}`,
        { method: "DELETE" },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to delete procedure: ${response.status}`);
      }

      onProcedureDeleted(procedure.id);
      toast.success("Procedure verwijderd");
    } catch (error) {
      console.error("Error deleting procedure:", error);
      toast.error("Fout bij het verwijderen van procedure");
    } finally {
      setIsDeleting(false);
    }
  }, [procedure.id, session, onProcedureDeleted]);

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Gavel weight="duotone" size={18} className="text-moss-primary flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {LEGAL_PROCEDURE_TYPE_LABELS[procedure.type]}
              </span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  LEGAL_PROCEDURE_STATUS_COLORS[procedure.status]
                }`}
              >
                {LEGAL_PROCEDURE_STATUS_LABELS[procedure.status]}
              </span>
              {procedure.partyRole && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600">
                  {PARTY_ROLE_LABELS[procedure.partyRole]}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
              {procedure.caseNumber && (
                <span>Nr. {procedure.caseNumber}</span>
              )}
              {procedure.court && <span>- {procedure.court}</span>}
              {procedure.claimedAmount && (
                <span>
                  - Gevorderd: {applyMask(Number(procedure.claimedAmount), "valuta")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-neutral-secondary-mid">
            {procedure.procedureDates.length} gebeurtenis
            {procedure.procedureDates.length !== 1 ? "sen" : ""}
          </span>
          {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </div>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t px-3 pb-3">
          {isEditing ? (
            <LegalProcedureEditForm
              procedure={procedure}
              onSave={handleSaveEdit}
              onCancel={() => setIsEditing(false)}
              isSaving={isSavingEdit}
            />
          ) : (
            <>
              {/* Procedure info */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 py-3 text-xs">
                {procedure.awardedAmount && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Toegekend:</span>
                    <span>{applyMask(Number(procedure.awardedAmount), "valuta")}</span>
                  </div>
                )}
                {procedure.uitvoerbaarVerklaard && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Uitvoerbaar verklaard:</span>
                    <span>Ja</span>
                  </div>
                )}
                {procedure.lawyer && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Advocaat:</span>
                    <Link
                      to={`/contact/${procedure.lawyer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-moss-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {procedure.lawyer.firstName} {procedure.lawyer.lastName}
                      <ArrowSquareOut size={10} />
                    </Link>
                  </div>
                )}
                {procedure.lawyerReference && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Ref. advocaat:</span>
                    <span>{procedure.lawyerReference}</span>
                  </div>
                )}
                {procedure.dossiervoorbereidingUserId && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Dossiervoorbereiding:</span>
                    <span>{getUserName(procedure.dossiervoorbereidingUserId) || "-"}</span>
                  </div>
                )}
                {!procedure.lawyerId && procedure.pleitenUserId && (
                  <div className="flex justify-between">
                    <span className="text-neutral-secondary-mid">Pleiten:</span>
                    <span>{getUserName(procedure.pleitenUserId) || "-"}</span>
                  </div>
                )}
              </div>

              {procedure.remarks && (
                <p className="text-xs text-neutral-secondary-mid mb-3 border-t pt-2">
                  {procedure.remarks}
                </p>
              )}

              {/* Edit button */}
              <div className="border-t pt-2 mb-1">
                <ButtonNew
                  variant="secondaryMoss"
                  icon={<PencilSimple size={12} />}
                  label="Procedure bewerken"
                  onClick={() => setIsEditing(true)}
                />
              </div>
            </>
          )}

          {/* Issues */}
          <div className="border-t pt-3">
            <ProcedureIssues
              issues={procedure.issues || []}
              onIssuesChanged={handleIssuesChanged}
            />
          </div>

          {/* Procedure Timeline */}
          <div className="border-t pt-3">
            <h5 className="text-xs font-medium text-neutral-secondary-mid mb-2">
              Verloop procedure
            </h5>
            <ProcedureTimeline
              procedure={procedure}
              onAddDate={handleAddDate}
              onToggleDateCompleted={handleToggleDateCompleted}
              onDeleteDate={handleDeleteDate}
              onToggleConclusieTermijnen={handleToggleConclusieTermijnen}
              isSavingDate={isSavingDate}
            />
          </div>

          {/* Delete procedure */}
          <div className="border-t pt-3 mt-3 flex justify-end">
            <ButtonNew
              variant="secondaryStatusRed"
              icon={<Trash size={12} />}
              label={isDeleting ? "Verwijderen..." : "Procedure verwijderen"}
              onClick={handleDeleteProcedure}
              isDisabled={isDeleting}
            />
          </div>
        </div>
      )}
    </div>
  );
};
